import { useQuery, useQueryClient } from "@tanstack/react-query";
import { parseAsArrayOf, parseAsInteger, parseAsString, parseAsStringEnum, useQueryStates } from "nuqs";

import { supabase } from "~/store/initializeStore";

const ITEMS_PER_PAGE = 25;

export { ITEMS_PER_PAGE };

export type SortBy = "createdAt" | "totalRevenue";
export type SortDir = "asc" | "desc";

export interface CustomerListItem {
  id: string;
  email: string | null;
  emailAliases?: string[] | null;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  source: string | null;
  createdAt: string | null;
  totalRevenue: number;
  stripeCustomerId: string | null;
  client: unknown;
  metadata: unknown;
  bundlesCount: number;
}

interface RpcRow extends Omit<CustomerListItem, "bundlesCount"> {
  bundles_count: number;
  total_count: number;
}

interface RawPostgRESTRow {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  source: string | null;
  createdAt: string | null;
  totalRevenue: number;
  stripeCustomerId: string | null;
  client: unknown;
  metadata: unknown;
  bundlesCount: { count: number }[] | null;
}

export const useProductsCustomers = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useQueryStates(
    {
      page: parseAsInteger.withDefault(1),
      searchTerm: parseAsString.withDefault(""),
      bundleProductId: parseAsString,
      productSequence: parseAsArrayOf(parseAsString).withDefault([]),
      firstPurchaseFrom: parseAsString,
      firstPurchaseTo: parseAsString,
      sortBy: parseAsStringEnum<SortBy>(["createdAt", "totalRevenue"]).withDefault("createdAt"),
      sortDir: parseAsStringEnum<SortDir>(["asc", "desc"]).withDefault("desc"),
    },
    { history: "push", shallow: false, clearOnDefault: true },
  );

  const { page, searchTerm, bundleProductId, productSequence, firstPurchaseFrom, firstPurchaseTo, sortBy, sortDir } =
    filters;

  const useAdvancedFilters = Boolean(
    bundleProductId || (productSequence && productSequence.length > 0) || firstPurchaseFrom || firstPurchaseTo,
  );

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "productsCustomers",
      page,
      searchTerm,
      bundleProductId,
      productSequence,
      firstPurchaseFrom,
      firstPurchaseTo,
      sortBy,
      sortDir,
    ] as const,
    keepPreviousData: true,
    queryFn: async ({ signal }) => {
      const start = (page - 1) * ITEMS_PER_PAGE;
      const stop = page * ITEMS_PER_PAGE - 1;

      if (useAdvancedFilters) {
        const rpcBuilder = supabase.rpc(
          // @ts-expect-error - RPC type not yet generated in Database types
          "search_customers_with_bundles",
          {
            p_search_term: searchTerm ? searchTerm : null,
            p_bundle_product_id: bundleProductId,
            p_product_sequence: productSequence && productSequence.length > 0 ? productSequence : null,
            p_first_purchase_from: firstPurchaseFrom,
            p_first_purchase_to: firstPurchaseTo,
            p_sort_by: sortBy,
            p_sort_dir: sortDir,
            p_page_start: start,
            p_page_end: stop,
          },
        );
        const rpcQuery = signal ? rpcBuilder.abortSignal(signal) : rpcBuilder;
        const { data: rpcData, error } = await rpcQuery;

        if (error) {
          throw new Error(error.message);
        }

        const rows = (rpcData ?? []) as RpcRow[];
        const total = rows[0]?.total_count ?? 0;

        return {
          list: rows.map((row) => {
            const { total_count: _t, bundles_count, ...rest } = row;
            return { ...rest, bundlesCount: Number(bundles_count ?? 0) } satisfies CustomerListItem;
          }),
          count: Number(total),
        };
      }

      let query = supabase
        .from("customer")
        .select(
          "id, email, firstName, lastName, phoneNumber, source, createdAt, totalRevenue, stripeCustomerId, client, metadata, bundlesCount:customer_bundle(count)",
          { count: searchTerm ? "exact" : "estimated" },
        )
        .order(sortBy, { ascending: sortDir === "asc" });

      if (searchTerm) {
        query = query.or(`email.ilike.%${searchTerm}%,firstName.ilike.%${searchTerm}%,lastName.ilike.%${searchTerm}%`);
      }

      const rangedQuery = query.range(start, stop);
      const finalQuery = signal ? rangedQuery.abortSignal(signal) : rangedQuery;
      const { data: rows, count, error } = await finalQuery;

      if (error) {
        throw new Error(error.message);
      }

      const list = ((rows ?? []) as unknown as RawPostgRESTRow[]).map((row) => ({
        id: row.id,
        email: row.email,
        firstName: row.firstName,
        lastName: row.lastName,
        phoneNumber: row.phoneNumber,
        source: row.source,
        createdAt: row.createdAt,
        totalRevenue: row.totalRevenue,
        stripeCustomerId: row.stripeCustomerId,
        client: row.client,
        metadata: row.metadata,
        bundlesCount: row.bundlesCount?.[0]?.count ?? 0,
      }));

      return {
        list,
        count: count ?? 0,
      };
    },
  });

  const setPage = (nextPage: number) => {
    void setFilters({ page: nextPage });
  };

  const setSearchTerm = (value: string) => {
    void setFilters({ searchTerm: value || null, page: 1 });
  };

  const setBundleProductId = (value: string | null) => {
    void setFilters({ bundleProductId: value, page: 1 });
  };

  const setProductSequence = (value: string[]) => {
    void setFilters({ productSequence: value.length ? value : null, page: 1 });
  };

  const setFirstPurchaseRange = (from: string | null, to: string | null) => {
    void setFilters({ firstPurchaseFrom: from, firstPurchaseTo: to, page: 1 });
  };

  const setSort = (nextSortBy: SortBy | null, nextSortDir: SortDir | null) => {
    void setFilters({ sortBy: nextSortBy, sortDir: nextSortDir, page: 1 });
  };

  const resetFilters = () => {
    void setFilters({
      page: 1,
      searchTerm: null,
      bundleProductId: null,
      productSequence: null,
      firstPurchaseFrom: null,
      firstPurchaseTo: null,
      sortBy: null,
      sortDir: null,
    });
  };

  const refresh = () => {
    void queryClient.invalidateQueries(["productsCustomers"]);
  };

  return {
    data,
    isLoading,
    isFetching,
    page,
    pageSize: ITEMS_PER_PAGE,
    searchTerm,
    bundleProductId,
    productSequence: productSequence ?? [],
    firstPurchaseFrom,
    firstPurchaseTo,
    sortBy,
    sortDir,
    setPage,
    setSearchTerm,
    setBundleProductId,
    setProductSequence,
    setFirstPurchaseRange,
    setSort,
    resetFilters,
    refresh,
  };
};
